import { create } from 'zustand';
import {
  fetchWorkflowSuggestions,
  logWorkflowAction,
  fetchWorkflowPatterns,
  fetchWorkflowPreferences,
  updateWorkflowPreferences,
  recordPrescriptionTabUsage as recordPrescriptionTabUsageApi,
  WORKFLOW_ACTIONS,
} from '../services/api/workflowApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Workflow Store — Zustand state for Smart Visit Workflow Engine
 *
 * Manages:
 *   • Per-visit suggestions (cached in-memory)
 *   • Action logging (fire-and-forget to backend)
 *   • Auto-progression state
 *   • User workflow preferences
 *
 * Design principles:
 *   • Non-blocking: All workflow operations are best-effort
 *   • Fast: Suggestions served from in-memory cache, refreshed async
 *   • Fail-safe: Store failures never cascade to visit operations
 * ═══════════════════════════════════════════════════════════════════════
 */

const useWorkflowStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  
  /** Map of visitId → { suggestions, currentStage, autoProgressEnabled, fetchedAt } */
  visitSuggestions: {},

  /** User's learned workflow patterns (fetched once per session) */
  patterns: null,
  patternsFetched: false,

  /** User's auto-progression preferences */
  preferences: {
    autoProgressEnabled: false,
    autoProgressThreshold: 5,
    suppressedSuggestions: [],
  },

  /** Preferred prescription tab — 'add' (digital) or 'upload' (file). Backend returns role-aware default. */
  prescriptionTabPreference: 'upload',
  prescriptionTabPreferenceFetched: false,

  /** Loading states */
  loadingSuggestions: {},
  loadingPatterns: false,

  /** Last action performed per visit (for UI display) */
  lastActions: {},

  // ── Actions ────────────────────────────────────────────────────────

  /**
   * Log a completed action and refresh suggestions.
   * Fire-and-forget: never blocks the caller.
   *
   * @param {string} visitId
   * @param {string} action - WORKFLOW_ACTIONS enum value
   * @param {object} [context] - Optional context
   */
  logAction: (visitId, action, context = null) => {
    // Update last action immediately (optimistic)
    set((state) => ({
      lastActions: { ...state.lastActions, [visitId]: action },
    }));

    // Fire-and-forget to backend
    logWorkflowAction(visitId, action, context);

    // Refresh suggestions async (debounced)
    const { _refreshSuggestions } = get();
    setTimeout(() => _refreshSuggestions(visitId, action), 300);
  },

  /**
   * Fetch suggestions for a visit.
   * Results are cached in-memory and served instantly on subsequent calls.
   *
   * @param {string} visitId
   * @param {string} [lastAction]
   * @param {string} [visitStatus]
   */
  fetchSuggestions: async (visitId, lastAction = null, visitStatus = null) => {
    // Check cache freshness (5 min TTL)
    const cached = get().visitSuggestions[visitId];
    if (cached && Date.now() - cached.fetchedAt < 300_000) {
      return cached;
    }

    set((state) => ({
      loadingSuggestions: { ...state.loadingSuggestions, [visitId]: true },
    }));

    try {
      const data = await fetchWorkflowSuggestions(visitId, lastAction, visitStatus);

      const entry = {
        suggestions: data.suggestions || [],
        currentStage: data.current_stage,
        autoProgressEnabled: data.auto_progress_enabled || false,
        fetchedAt: Date.now(),
      };

      set((state) => ({
        visitSuggestions: { ...state.visitSuggestions, [visitId]: entry },
        loadingSuggestions: { ...state.loadingSuggestions, [visitId]: false },
      }));

      return entry;
    } catch (err) {
      set((state) => ({
        loadingSuggestions: { ...state.loadingSuggestions, [visitId]: false },
      }));
      return { suggestions: [], currentStage: null, autoProgressEnabled: false };
    }
  },

  /**
   * Force refresh suggestions (called after action log).
   * @internal
   */
  _refreshSuggestions: async (visitId, lastAction = null) => {
    try {
      const data = await fetchWorkflowSuggestions(visitId, lastAction);
      const entry = {
        suggestions: data.suggestions || [],
        currentStage: data.current_stage,
        autoProgressEnabled: data.auto_progress_enabled || false,
        fetchedAt: Date.now(),
      };
      set((state) => ({
        visitSuggestions: { ...state.visitSuggestions, [visitId]: entry },
      }));
    } catch {
      // Silent failure
    }
  },

  /**
   * Clear suggestions for a visit (e.g. when visit card closes).
   */
  clearSuggestions: (visitId) => {
    set((state) => {
      const next = { ...state.visitSuggestions };
      delete next[visitId];
      return { visitSuggestions: next };
    });
  },

  /**
   * Fetch user's learned workflow patterns.
   */
  fetchPatterns: async () => {
    // Patterns are static for a session (tab ordering doesn't need them
    // live). 4 dashboard views each call this on mount; the flag collapses
    // them to a single fetch. Mirrors prescriptionTabPreferenceFetched.
    if (get().patternsFetched) return get().patterns;

    set({ loadingPatterns: true });
    try {
      const data = await fetchWorkflowPatterns();
      set({ patterns: data, loadingPatterns: false, patternsFetched: true });
      return data;
    } catch {
      set({ loadingPatterns: false });
      return null;
    }
  },

  /**
   * Update auto-progression preferences.
   */
  updatePreferences: async (prefs) => {
    try {
      const data = await updateWorkflowPreferences(prefs);
      set({
        preferences: {
          autoProgressEnabled: data.auto_progress_enabled,
          autoProgressThreshold: data.auto_progress_threshold,
          suppressedSuggestions: data.suppressed_suggestions || [],
        },
        prescriptionTabPreference: data.preferred_prescription_tab || 'upload',
      });
      return data;
    } catch (err) {
      console.error('Failed to update workflow preferences:', err);
      throw err;
    }
  },

  /**
   * Fetch workflow preferences from backend (includes prescription tab default).
   * Called once on mount — idempotent.
   */
  fetchPreferences: async () => {
    if (get().prescriptionTabPreferenceFetched) return;
    try {
      const data = await fetchWorkflowPreferences();
      set({
        preferences: {
          autoProgressEnabled: data.auto_progress_enabled,
          autoProgressThreshold: data.auto_progress_threshold,
          suppressedSuggestions: data.suppressed_suggestions || [],
        },
        prescriptionTabPreference: data.preferred_prescription_tab || 'upload',
        prescriptionTabPreferenceFetched: true,
      });
    } catch {
      // Non-blocking — use defaults
      set({ prescriptionTabPreferenceFetched: true });
    }
  },

  /**
   * Record which prescription tab was used (fire-and-forget).
   * Backend auto-flips the preference after 2 consecutive uses.
   */
  recordPrescriptionTabUsage: async (tabUsed) => {
    try {
      const data = await recordPrescriptionTabUsageApi(tabUsed);
      if (data?.preferred_prescription_tab) {
        set({ prescriptionTabPreference: data.preferred_prescription_tab });
      }
    } catch {
      // Non-blocking
    }
  },

  /**
   * Dismiss a suggestion for the current session (doesn't persist).
   */
  dismissSuggestion: (visitId, actionToDismiss) => {
    set((state) => {
      const current = state.visitSuggestions[visitId];
      if (!current) return {};
      return {
        visitSuggestions: {
          ...state.visitSuggestions,
          [visitId]: {
            ...current,
            suggestions: current.suggestions.filter(
              (s) => s.action !== actionToDismiss
            ),
          },
        },
      };
    });
  },
}));

export { WORKFLOW_ACTIONS };
export default useWorkflowStore;
